import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  Grid,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  tableCellClasses,
  CircularProgress,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
} from "@mui/material";
import { PrimaryButton } from "components/Buttons";
import Colors from "assets/Style/Colors";
import { CheckIcon, FontFamily, PendingIcon } from "assets";
import { ErrorToaster, SuccessToaster } from "components/Toaster";
import { Controller, useForm } from "react-hook-form";
import { CircleLoading } from "components/Loaders";
import Pagination from "components/Pagination";
import { makeStyles } from "@mui/styles";
import styled from "@emotion/styled";
import moment from "moment/moment";
import { useNavigate, useLocation } from "react-router-dom";
import InputField from "components/Input";
import ExportServices from "services/Export";
import { CommaSeparator } from "utils";
import { numberRegex } from "utils";

// *For Table Style
const Row = styled(TableRow)(({ theme }) => ({
  border: 0,
}));

const Cell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    border: 0,
    padding: "15px",
    textAlign: "center",
    whiteSpace: "nowrap",
    background: Colors.primary,
    color: Colors.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    textAlign: "center",
    textWrap: "nowrap",
    padding: "5px !important",

    ".MuiBox-root": {
      display: "flex",
      gap: "6px",
      alignItems: "center",
      justifyContent: "center",
      ".MuiBox-root": {
        cursor: "pointer",
      },
    },
    svg: {
      width: "auto",
      height: "24px",
    },
    ".MuiTypography-root": {
      textTransform: "capitalize",
      fontFamily: FontFamily.NunitoRegular,
      textWrap: "nowrap",
    },
    ".MuiButtonBase-root": {
      padding: "8px",
      width: "28px",
      height: "28px",
    },
  },
}));

const useStyles = makeStyles({
  loaderWrap: {
    display: "flex",
    height: 100,
    "& svg": {
      width: "40px !important",
      height: "40px !important",
    },
  },
});

function ExportVendorFundsApply() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { state } = useLocation();
  const contentRef = useRef(null);

  const isBroker =
    state?.vendor?.customerProfile?.type?.toLowerCase() === "broker";
  const isAgent =
    state?.vendor?.customerProfile?.type?.toLowerCase() === "agent";
  const isPortAgent = state?.vendor?.customerProfile?.type === "Port Agent";
  const isRecoveryAgent =
    state?.vendor?.customerProfile?.type === "Recovery Agent";
  let remainingFcyAmount = state?.fcy_amount;

  const tableHead = [
    "Select",
    "Receive Date",
    "VIN",
    "Make",
    "Model",
    "Color",
    ...(state?.vendor?.customerProfile?.type?.toLowerCase() === "agent"
      ? ["Menifest"]
      : []),
    ...(state?.vendor?.customerProfile?.type?.toLowerCase() === "broker"
      ? ["CMR#"]
      : []),
    ...(isBroker ? ["CMR Date"] : []),
    ...(isAgent ? ["Menifest Date"] : []),
    ...(isPortAgent ? ["Country"] : []),
    "Final Dest",
    ...(isAgent
      ? ["Agent Charges$"]
      : isBroker
      ? ["Broker Charges$"]
      : isRecoveryAgent
      ? ["Recovery Charges$", "Shifting Charges$"]
      : isPortAgent
      ? ["Country Costing$"]
      : []),

    ...(isRecoveryAgent
      ? ["Recovery Charges-AED", "Shifting Charges-AED"]
      : isPortAgent
      ? ["Country Costing-AED"]
      : []),

    ...(isAgent ? ["Border Charges$"] : []),

    "Total Charges$",
    "Applied Status",

    ...(isRecoveryAgent
      ? ["Applied Recovery", "Applied Shifting", "Applied Amount"]
      : ["Applied Amount"]),

    "Balance$",
  ];

  const [selectedClientBooking, setSelectedClientBooking] = useState([]);
  // *For Select and DeSelect client
  const handleSelectClientBooking = (fcyammount, id) => {
    try {
      const shallowCopy = [...selectedClientBooking];
      const index = shallowCopy.indexOf(id);
      const calcCopy = [...calcAmount];
      const rowIndex = calcCopy.findIndex((item) => item.id === id);
      if (rowIndex === -1) return;

      const currentItem = calcCopy[rowIndex];
      const agentCharges = parseFloat(currentItem?.agent_charges ?? 0);
      const borderCharges = parseFloat(currentItem?.border_cost_usd ?? 0);
      const fullAgentTotal = agentCharges + borderCharges;

      if (index === -1) {
        // ✅ SELECTING a row
        shallowCopy.push(id);

        if (isAgent) {
          currentItem.applied = Number(fullAgentTotal.toFixed(2));
          currentItem.balance = 0; // fully paid when selected
          setValue2(`applied-${id}`, fullAgentTotal.toFixed(2));
        } else {
          // initialize other roles to zero
          currentItem.applied = 0;
          currentItem.appliedRecovery = 0;
          currentItem.appliedShifting = 0;

          const totalCharges =
            (parseFloat(currentItem?.recovery_charges ?? 0) || 0) +
            (parseFloat(currentItem?.shifting_charges ?? 0) || 0) +
            (parseFloat(currentItem?.broker_charges ?? 0) || 0) +
            (parseFloat(currentItem?.agent_charges ?? 0) || 0) +
            (parseFloat(currentItem?.border_cost_usd ?? 0) || 0);

          currentItem.balance = Number(totalCharges.toFixed(2));

          setValue2(`applied-${id}`, "0.00");
          setValue2(`applied-recovery-${id}`, "0.00");
          setValue2(`applied-shifting-${id}`, "0.00");
        }
      } else {
        // ❌ UNSELECTING a row
        shallowCopy.splice(index, 1);

        currentItem.applied = 0;
        currentItem.appliedRecovery = 0;
        currentItem.appliedShifting = 0;

        if (isAgent) {
          // restore full total as balance
          currentItem.balance = Number(fullAgentTotal.toFixed(2));
        } else {
          const totalCharges =
            (parseFloat(currentItem?.recovery_charges ?? 0) || 0) +
            (parseFloat(currentItem?.shifting_charges ?? 0) || 0) +
            (parseFloat(currentItem?.broker_charges ?? 0) || 0) +
            (parseFloat(currentItem?.agent_charges ?? 0) || 0) +
            (parseFloat(currentItem?.border_cost_usd ?? 0) || 0);

          currentItem.balance = Number(totalCharges.toFixed(2));
        }

        setValue2(`applied-${id}`, "0.00");
        setValue2(`applied-recovery-${id}`, "0.00");
        setValue2(`applied-shifting-${id}`, "0.00");
      }

      setCalcAmount(calcCopy);
      setSelectedClientBooking(shallowCopy);

      // 🔄 Recalculate totals only from SELECTED rows
      let totalApp = 0,
        totalBal = 0;

      calcCopy.forEach((e) => {
        if (!shallowCopy.includes(e.id)) {
          // if not selected → add its balance
          totalBal += parseFloat(e?.balance ?? 0);
          return;
        }

        if (isAgent) {
          totalApp +=
            parseFloat(e?.agent_charges ?? 0) +
            parseFloat(e?.border_cost_usd ?? 0);
          // agent balance is always 0 when selected
        } else {
          totalApp += parseFloat(e?.applied ?? 0);
          totalBal += parseFloat(e?.balance ?? 0);
        }
      });

      setTotalApplied(Number(totalApp.toFixed(2)));
      setTotalBalance(Number(totalBal.toFixed(2)));
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues: getValues1,
  } = useForm();
  const {
    register: register2,
    setValue: setValue2,
    getValues: getValues2,
    control: control2,
    watch,
  } = useForm();
  const watchedNotes = watch("notes");

  const [loader, setLoader] = useState(false);
  const [loading, setLoading] = useState(false);

  // *For Vendor Costing
  const [vendorCosting, setVendorCosting] = useState();
  const [totalAmount, setTotalAmount] = useState();
  const [calcAmount, setCalcAmount] = useState({});
  const [totalApplied, setTotalApplied] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);
  // *For Pagination
  const [totalCount, setTotalCount] = useState(0);
  const [pageLimit, setPageLimit] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [appliedValue, setAppliedValue] = useState({});
  const [rowBalanced, setRowBalanced] = useState({});

  // *For Vendor Dropdown
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);

  let prev_balance = state?.fcy_amount;
  let lastBalance = state?.fcy_amount;
  // *For Filters
  const [filters, setFilters] = useState({});

  const [fcyammount, setFcyammount] = useState(0);

  // *For Set Vendor FCY Amount
  const [vendorFcyAmount, setVendorFcyAmount] = useState();

  const [visibleColumns, setVisibleColumns] = useState([
    ...Array(tableHead?.length).keys(),
  ]);

  // *For Vendor
  const getVendorDropdown = async () => {
    try {
      const { data } = await ExportServices.getVendorDropdown();
      setVendors([
        ...data?.agents,
        ...data?.brokers,
        ...data?.portAgents,
        ...data?.recoveryAgents,
      ]);
      let list = [
        ...data?.agents,
        ...data?.brokers,
        ...data?.portAgents,
        ...data?.recoveryAgents,
      ];
      let selected = list.find((item) => item?.id == state?.vendor?.id);
      setSelectedVendor(selected?.customerProfile?.broker_type_id);
      if (state) {
        let data = {
          vendor_id: state?.vendor?.id,
          type: state?.vendor?.type,
          broker_type_id: selected?.customerProfile?.broker_type_id,
        };
        setValue("vendor", state?.vendor?.name);
        // getVendorCosting(
        //   1,
        //   "",
        //   data,
        //   selected?.customerProfile?.broker_type_id
        // );

        setVendorFcyAmount(state?.fcy_amount);
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };

  // *For Vendor Costing
  const getVendorCosting = async (page, limit, filter, broker_type_id) => {
    setLoader(true);
    try {
      const Page = page ? page : currentPage;
      const Limit = limit ? limit : pageLimit;
      const Filter = { ...filters, ...filter };
      setCurrentPage(Page);
      setPageLimit(Limit);
      setFilters(Filter);
      let params = {
        page: Page,
        limit: Limit,
        payable: true,
      };
      params = { ...params, ...Filter };
      const { data } = await ExportServices.getVendorPayments(params);
      setTotalCount(data?.vehicles?.count);
      setVendorCosting(data?.vehicles?.rows);

      const calcTotal = [];
      let totalBal = 0;
      let shipping_charges = 0,
        towing_charges = 0,
        clearance_charges = 0,
        late_fee = 0,
        storage = 0,
        category_a = 0,
        broker_fee = 0,
        title_fee = 0,
        inspection = 0,
        other_charges = 0,
        custom_duty = 0,
        total = 0;
      let borderCharges = 0;
      let totalT;
      let paidAmount;
      let balance;

      data?.vehicles?.rows.forEach((e) => {
        if (broker_type_id === 1) {
          // === Agent
          const agentCharges = parseFloat(e?.payment?.agent_charges ?? 0);
          const agentPaid = parseFloat(e?.payment?.agent_charged_paid ?? 0);
          borderCharges = parseFloat(e?.border_costing?.cost_usd ?? 0);
          const totalCharges = agentCharges + borderCharges;

          total += totalCharges;
          totalT = totalCharges;

          paidAmount = agentPaid; // already recorded separately
          balance = totalCharges - agentPaid;

          totalBal += balance;
        } else if (broker_type_id === 2) {
          // === Broker
          total += e?.payment?.broker_charges;
          totalT = e?.payment?.broker_charges;
          paidAmount = e?.payment?.broker_charged_paid;
          balance =
            parseFloat(e?.payment?.broker_charges ?? 0) -
            parseFloat(e?.payment?.broker_charged_paid ?? 0);

          totalBal += balance;
        } else if (broker_type_id === 5) {
          // === Recovery Agent → Recovery + Shifting
          const recovery = parseFloat(e?.payment?.recovery_charges ?? 0);
          const shifting = parseFloat(e?.payment?.shifting_charges ?? 0);
          totalT = recovery + shifting;
          total += totalT;

          const recoveryPaid = parseFloat(
            e?.payment?.recovery_charged_paid ?? 0
          );
          const shiftingPaid = parseFloat(
            e?.payment?.shifting_charged_paid ?? 0
          );
          paidAmount = recoveryPaid + shiftingPaid;

          balance = totalT - paidAmount;
          totalBal += balance;
        } else if (broker_type_id === 6) {
          // === Port Agent → Country Costing
          total += e?.payment?.country_charges ?? 0;
          totalT = e?.payment?.country_charges ?? 0;
          paidAmount = e?.payment?.country_charged_paid ?? 0;

          balance =
            parseFloat(e?.payment?.country_charges ?? 0) -
            parseFloat(e?.payment?.country_charged_paid ?? 0);

          totalBal += balance;
        }

        let obj = {
          id: e?.id,
          ev_payment_id: e?.payment?.id,
          applied: 0,
          balance: balance,
          paidAmount: paidAmount,
          total: totalT,
          ...(isAgent && {
            border_cost_usd: borderCharges ?? 0,
            agent_charges: e?.payment?.agent_charges ?? 0,
          }),
          ...(isBroker && {
            broker_charges: e?.payment?.broker_charges ?? 0,
          }),
          ...(isRecoveryAgent && {
            recovery_charges: e?.payment?.recovery_charges ?? 0,
            shifting_charges: e?.payment?.shifting_charges ?? 0,
          }),
          ...(isPortAgent && {
            country_charges: e?.payment?.country_charges ?? 0,
          }),
        };

        calcTotal.push(obj);
      });

      let obj = {
        shipping_charges: shipping_charges,
        towing_charges: towing_charges,
        clearance_charges: clearance_charges,
        late_fee: late_fee,
        storage: storage,
        category_a: category_a,
        broker_fee: broker_fee,
        title_fee: title_fee,
        inspection: inspection,
        other_charges: other_charges,
        custom_duty: custom_duty,
        total: total,
        ev_payment_id: data?.vehicles?.rows[0]?.payment?.id,
      };
      setTotalAmount(obj);
      setCalcAmount(calcTotal);
      setTotalBalance(totalBal);

      if (data?.vehicles?.rows?.length === 0) {
        ErrorToaster(
          isAgent || isPortAgent
            ? "Manifest already processed."
            : isBroker
            ? "CMR already processed."
            : "VIN already processed."
        );
      }
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  // *For Apply Filters
  const applyFilter = async () => {
    try {
      let data = {
        vendor_id: state?.vendor?.id,
        country_id: state?.vendor?.country_id,
        type: state?.vendor?.type,
        broker_type_id: selectedVendor,
        filter:
          getValues1("vin") ||
          getValues1("manifest_no") ||
          getValues1("CMR_no"),
      };
      getVendorCosting(1, "", data, selectedVendor);
    } catch (error) {
      ErrorToaster(error);
    }
  };
  const handleCalc = (val, id, grandTotal, type = "applied") => {
    try {
      const shallowCopy = [...calcAmount];
      const index = shallowCopy.findIndex((e) => e.id === id);
      if (index === -1) return;

      const current = shallowCopy[index];

      let balancesAmt = parseFloat(grandTotal) - parseFloat(val);

      setRowBalanced((prevState) => {
        const updatedState = { ...prevState, [id]: balancesAmt };
        return updatedState;
      });
      //     setRowBalanced((prevState) => ({
      //     ...prevState,
      //     [id]: balancesAmt,
      // }));

      // --- Agent: fixed applied (no manual changes)
      if (isAgent) {
        const agentCharges = parseFloat(current?.agent_charges ?? 0);
        const borderCost = parseFloat(current?.border_cost_usd ?? 0);
        const agentApplied = agentCharges + borderCost;

        current.applied = Number(agentApplied.toFixed(2));
        current.balance = 0;
        setValue2(`applied-${id}`, agentApplied.toFixed(2));

        setCalcAmount(shallowCopy);

        // recalc totals
        let totalApp = 0,
          totalBal = 0;
        shallowCopy.forEach((e) => {
          totalApp +=
            parseFloat(e?.agent_charges ?? 0) +
            parseFloat(e?.border_cost_usd ?? 0);
        });

        setTotalApplied(Number(totalApp.toFixed(2)));
        setTotalBalance(Number(totalBal.toFixed(2)));
        return;
      }

      // --- Broker
      if (isBroker) {
        const brokerMax = parseFloat(current?.broker_charges ?? 0);
        const appliedValue = Math.min(parseFloat(val) || 0, brokerMax);

        current.applied = Number(appliedValue.toFixed(2));
        current.balance = Number((brokerMax - appliedValue).toFixed(2));

        setValue2(`applied-${id}`, appliedValue.toFixed(2));
        setCalcAmount(shallowCopy);

        // recalc totals
        let totalApp = 0,
          totalBal = 0;
        shallowCopy.forEach((e) => {
          totalApp += Number(parseFloat(e?.applied ?? 0));
          totalBal += Number(parseFloat(e?.balance ?? 0));
        });

        setTotalApplied(Number(totalApp.toFixed(2)));
        setTotalBalance(Number(totalBal.toFixed(2)));
        return;
      }

      // --- Port Agent
      if (isPortAgent) {
        const countryMax = parseFloat(current?.country_charges ?? 0);
        const appliedValue = Math.min(parseFloat(val) || 0, countryMax);

        current.applied = Number(appliedValue.toFixed(2));
        current.balance = Number((countryMax - appliedValue).toFixed(2));

        setValue2(`applied-${id}`, appliedValue.toFixed(2));
        setCalcAmount(shallowCopy);

        // recalc totals
        let totalApp = 0,
          totalBal = 0;
        shallowCopy.forEach((e) => {
          totalApp += Number(parseFloat(e?.applied ?? 0));
          totalBal += Number(parseFloat(e?.balance ?? 0));
        });

        setTotalApplied(Number(totalApp.toFixed(2)));
        setTotalBalance(Number(totalBal.toFixed(2)));
        return;
      }

      // --- Recovery Agent
      const value = val ? parseFloat(val) : 0;
      const recoveryMax = parseFloat(current?.recovery_charges ?? 0);
      const shiftingMax = parseFloat(current?.shifting_charges ?? 0);

      let recoveryApplied = parseFloat(current?.appliedRecovery ?? 0);
      let shiftingApplied = parseFloat(current?.appliedShifting ?? 0);

      if (type === "recovery") {
        recoveryApplied = Math.min(value, recoveryMax);
        current.appliedRecovery = Number(recoveryApplied.toFixed(2));
        setValue2(`applied-recovery-${id}`, recoveryApplied.toFixed(2));
      } else if (type === "shifting") {
        shiftingApplied = Math.min(value, shiftingMax);
        current.appliedShifting = Number(shiftingApplied.toFixed(2));
        setValue2(`applied-shifting-${id}`, shiftingApplied.toFixed(2));
      } else {
        const grandTotal = recoveryMax + shiftingMax;
        const appliedValue = Math.min(value, grandTotal);
        current.applied = Number(appliedValue.toFixed(2));
        setValue2(`applied-${id}`, appliedValue.toFixed(2));
      }

      if (isRecoveryAgent) {
        const totalApplied = recoveryApplied + shiftingApplied;
        current.applied = Number(totalApplied.toFixed(2));
        setValue2(`applied-${id}`, totalApplied.toFixed(2));
      }

      // Update row balance
      const totalCharges =
        (parseFloat(current?.recovery_charges ?? 0) || 0) +
        (parseFloat(current?.shifting_charges ?? 0) || 0) +
        (parseFloat(current?.broker_charges ?? 0) || 0) +
        (parseFloat(current?.agent_charges ?? 0) || 0) +
        (parseFloat(current?.border_cost_usd ?? 0) || 0) +
        (parseFloat(current?.country_charges ?? 0) || 0);

      current.balance = Number(
        (totalCharges - (current.applied ?? 0)).toFixed(2)
      );

      setCalcAmount(shallowCopy);

      // Recalculate totals
      let totalApp = 0,
        totalBal = 0,
        totalBorderCost = 0;

      shallowCopy.forEach((e) => {
        totalApp += Number(parseFloat(e?.applied ?? 0));
        totalBal += Number(parseFloat(e?.balance ?? 0));
        totalBorderCost += Number(parseFloat(e?.border_cost_usd ?? 0) || 0);
      });

      setTotalApplied(Number((totalApp + totalBorderCost).toFixed(2)));
      setTotalBalance(Number(totalBal.toFixed(2)));
    } catch (error) {
      ErrorToaster(error);
    }
  };

  // *For Apply Fund
  const applyFund = async () => {
    setLoading(true);
    try {
      const totalFcy = parseFloat(state?.fcy_amount ?? 0);
      const totalPaid = parseFloat(state?.paid_amount ?? 0);
      const availableFcy = totalFcy - totalPaid;

      let selectedTotalApplied = 0;
      const selectedDetails = [];

      for (const e of calcAmount) {
        if (selectedClientBooking.includes(e?.id)) {
          let applied = parseFloat(e?.applied ?? 0);
          let rowApplied = 0;

          if (isAgent) {
            // ✅ Auto-derive applied for agents
            rowApplied =
              parseFloat(e?.agent_charges ?? 0) +
              parseFloat(e?.border_cost_usd ?? 0);

            applied = rowApplied; // keep consistency
          } else {
            rowApplied = applied;

            // ❌ Reject only if applied is 0 for non-agents
            if (applied <= 0) {
              ErrorToaster(
                "0 amount is not allowed. Please enter a valid applied amount."
              );
              setLoading(false);
              return;
            }
          }

          selectedTotalApplied += rowApplied;

          // ✅ Build payload row-wise
          const applyObj = {
            ev_id: e?.id,
            ev_payment_id: e?.ev_payment_id,
            applied_amount: applied.toFixed(2),
          };

          if (isAgent) {
            applyObj.agent_charges = parseFloat(e?.agent_charges ?? 0).toFixed(
              2
            );
            applyObj.border_charges = parseFloat(
              e?.border_cost_usd ?? 0
            ).toFixed(2);
          }

          if (isBroker) {
            applyObj.broker_charges = applied.toFixed(2);
          }

          if (isPortAgent) {
            applyObj.country_charges = applied.toFixed(2);
          }

          if (isRecoveryAgent) {
            applyObj.recovery_charges = parseFloat(
              e?.appliedRecovery ?? 0
            ).toFixed(2);

            applyObj.shifting_charges = parseFloat(
              e?.appliedShifting ?? 0
            ).toFixed(2);
          }

          selectedDetails.push(applyObj);
        }
      }

      // ❌ Reject if total applied exceeds TT
      if (selectedTotalApplied > availableFcy) {
        ErrorToaster(
          `Total applied amount (${selectedTotalApplied.toFixed(
            2
          )}) cannot exceed TT amount (${availableFcy.toFixed(2)}).`
        );
        setLoading(false);
        return;
      }

      // ✅ Proceed
      const sum = selectedDetails.reduce(
        (total, item) => total + parseFloat(item?.applied_amount),
        0
      );

      const obj = {
        vendor_id: state?.vendor?.id,
        vendor_name: state?.vendor?.name,
        ex_rate: state?.ex_rate,
        tt_id: state?.client_amount ? null : state?.id,
        damage_id: state?.client_amount ? state?.id : null,
        external_ref_no: state?.external_no,
        paid_amount: parseFloat(sum).toFixed(2),
        currency: "usd",
        broker_type_id: selectedVendor,
        details: selectedDetails,
        notes: watchedNotes,
      };

      console.log(obj, "obj");
      // return;
      const { message } = await ExportServices.applyFund(obj);
      SuccessToaster(message);
      navigate("/export-vendor-applied-funds");
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoading(false);
    }
  };

  const columnRenderers = [
    // 0. Checkbox
    (item, isActive, total, status, rowBalance, fcyammount) => (
      <Checkbox
        checked={isActive}
        onChange={() => handleSelectClientBooking(fcyammount, item?.id)}
      />
    ),

    // 1. Receive Date
    (item) =>
      item?.created_at ? moment(item?.created_at).format("DD-MMM-YYYY") : "-",

    // 2. VIN
    (item) => item?.vin ?? "-",

    // 3. Make
    (item) => item?.make?.name ?? "-",

    // 4. Model
    (item) => item?.model?.name ?? "-",

    // 5. Color
    (item) => item?.color ?? "-",

    ...(isAgent ? [(item) => item?.manifest_number ?? "-"] : []),
    ...(isBroker ? [(item) => item?.cmr_number ?? "-"] : []),
    ...(isAgent
      ? [(item) => moment(item?.manifest_date).format("YYYY-MM-DD") ?? "-"]
      : []),
    ...(isBroker
      ? [(item) => moment(item?.cmr_date).format("YYYY-MM-DD") ?? "-"]
      : []),
    ...(isPortAgent ? [(item) => item?.country?.name ?? "-"] : []),
    (item) => item?.destination?.name ?? "-",

    // USD charges
    ...(isRecoveryAgent
      ? [
          (item) =>
            item?.payment?.recovery_charges
              ? parseFloat(item?.payment?.recovery_charges).toFixed(2)
              : "-",
          (item) =>
            item?.payment?.shifting_charges
              ? parseFloat(item?.payment?.shifting_charges).toFixed(2)
              : "-",
        ]
      : isPortAgent
      ? [
          (item) =>
            item?.payment?.country_charges
              ? parseFloat(item?.payment?.country_charges).toFixed(2)
              : "-",
        ]
      : isAgent
      ? [(item) => item?.payment?.agent_charges ?? "-"]
      : isBroker
      ? [(item) => item?.payment?.broker_charges ?? "-"]
      : []),

    // AED charges
    ...(isRecoveryAgent
      ? [
          (item) =>
            item?.payment?.recovery_charges
              ? (parseFloat(item?.payment?.recovery_charges) * 3.675).toFixed(2)
              : "-",
          (item) =>
            item?.payment?.shifting_charges
              ? (parseFloat(item?.payment?.shifting_charges) * 3.675).toFixed(2)
              : "-",
        ]
      : isPortAgent
      ? [
          (item) =>
            item?.payment?.country_charges
              ? (parseFloat(item?.payment?.country_charges) * 3.675).toFixed(2)
              : "-",
        ]
      : []),

    ...(isAgent ? [(item) => item?.border_costing?.cost_usd ?? "-"] : []),

    // Total charges
    (item) => {
      const recovery = parseFloat(item?.payment?.recovery_charges ?? 0);
      const shifting = parseFloat(item?.payment?.shifting_charges ?? 0);
      const country = parseFloat(item?.payment?.country_charges ?? 0);
      const agent = parseFloat(item?.payment?.agent_charges ?? 0);
      const broker = parseFloat(item?.payment?.broker_charges ?? 0);
      const border = parseFloat(item?.border_costing?.cost_usd ?? 0);

      let total = 0;
      if (isRecoveryAgent) total = recovery + shifting;
      else if (isPortAgent) total = country;
      else if (isAgent) total = agent + border;
      else if (isBroker) total = broker;

      return `${CommaSeparator(total.toFixed(2))}`;
    },

    // Applied status
    (_, __, ___, status) => (
      <Box
        sx={{ path: { fill: status === "Partial Paid" && Colors.bluishCyan } }}
      >
        {status === "Paid" ? (
          <CheckIcon />
        ) : status === "UnPaid" ? (
          <PendingIcon />
        ) : (
          <CheckIcon />
        )}
        <Typography variant="body2">{status}</Typography>
      </Box>
    ),

    // Applied fields
    ...(isRecoveryAgent
      ? [
          (item, isActive, total, status) => {
            const recovery = parseFloat(item?.payment?.recovery_charges ?? 0);

            return (
              status !== "Paid" && (
                <InputField
                  size="small"
                  type="number"
                  InputProps={{ inputProps: { min: 0, max: recovery } }}
                  inputStyle={{ width: "100px" }}
                  disabled={!isActive} // ✅ disable when checkbox is not checked
                  value={getValues2(`applied-recovery-${item?.id}`)}
                  register={register2(`applied-recovery-${item?.id}`, {
                    onChange: (e) =>
                      handleCalc(e.target.value, item?.id, "recovery"),
                  })}
                />
              )
            );
          },
          // Applied Shifting
          (item, isActive, total, status) => {
            const shifting = parseFloat(item?.payment?.shifting_charges ?? 0);

            return (
              status !== "Paid" && (
                <InputField
                  size="small"
                  type="number"
                  InputProps={{ inputProps: { min: 0, max: shifting } }}
                  inputStyle={{ width: "100px" }}
                  disabled={!isActive} // ✅ disable when checkbox is not checked
                  value={getValues2(`applied-shifting-${item?.id}`)}
                  register={register2(`applied-shifting-${item?.id}`, {
                    onChange: (e) =>
                      handleCalc(e.target.value, item?.id, "shifting"),
                  })}
                />
              )
            );
          },
          // Total Applied (auto sum, readonly for recovery agent)
          (item, isActive, total, status) => {
            const recovery = parseFloat(item?.payment?.recovery_charges ?? 0);
            const shifting = parseFloat(item?.payment?.shifting_charges ?? 0);
            const grandTotal = recovery + shifting;

            return (
              status !== "Paid" && (
                <InputField
                  size="small"
                  type="number"
                  InputProps={{ inputProps: { min: 0, max: grandTotal } }}
                  inputStyle={{ width: "100px" }}
                  disabled
                  value={getValues2(`applied-${item?.id}`)}
                />
              )
            );
          },
        ]
      : [
          // Default applied for others
          (item, isActive, total, status) => {
            const country = parseFloat(item?.payment?.country_charges ?? 0);
            const agent = parseFloat(item?.payment?.agent_charges ?? 0);
            const broker = parseFloat(item?.payment?.broker_charges ?? 0);
            const border = parseFloat(item?.border_costing?.cost_usd ?? 0);

            let grandTotal = 0;
            if (isPortAgent) grandTotal = country;
            else if (isAgent) grandTotal = agent + border;
            else if (isBroker) grandTotal = broker;

            return (
              status !== "Paid" && (
                <>
                  <InputField
                    size="small"
                    type="number"
                    InputProps={{
                      inputProps: { min: 0, max: grandTotal }, // Ensure the range is respected
                    }}
                    inputStyle={{ width: "100px" }}
                    disabled={
                      isAgent || // Agent always disabled
                      ((isBroker || isPortAgent) &&
                        !selectedClientBooking.includes(item.id)) // Broker/PortAgent disabled until checkbox is selected
                    }
                    value={
                      isAgent ? grandTotal.toFixed(2) : appliedValue[item?.id]
                    }
                    {...(!isAgent && {
                      register: register2(`applied-${item?.id}`, {
                        pattern: numberRegex,

                        onChange: (e) => {
                          let value = e.target.value;
                          if (value > grandTotal) {
                            value = grandTotal;
                          }
                          setAppliedValue((prevState) => ({
                            ...prevState,
                            [item?.id]: value,
                          }));
                          // setAppliedValue(value);
                          handleCalc(value, item?.id, grandTotal);
                        },
                      }),
                    })}
                  />

                  {/*// <InputField
              //   size="small"
              //   type="number"
              //   InputProps={{ inputProps: { min: 0, max: grandTotal } }}
              //   inputStyle={{ width: "100px" }}
              //   disabled={
              //     isAgent || // agent always disabled
              //     ((isBroker || isPortAgent) &&
              //       !selectedClientBooking.includes(item.id)) // broker/portagent disabled until checkbox select
              //   }
              //   value={
              //     isAgent
              //       ? grandTotal.toFixed(2)
              //       : getValues2(`applied-${item?.id}` || "")
              //   }
              //   {...(!isAgent && {
              //     register: register2(`applied-${item?.id}`, {
              //       pattern: numberRegex,
                   
              //          onChange: (e) => handleCalc(e.target.value, item?.id),
              //       }),
              //     })}
              //   />*/}
                </>
              )
            );
          },
        ]),
    (item) => {
      const balance = rowBalanced[Number(item.id)] ?? 0;
      return CommaSeparator(balance.toFixed(2));
    },
  ];

  const renderCellContent = (
    colIndex,
    item,
    isActive,
    total,
    status,
    rowBalance,
    fcyammount
  ) => {
    const renderer = columnRenderers[colIndex];
    return (
      renderer &&
      renderer(item, isActive, total, status, rowBalance, fcyammount)
    );
  };

  const handleColumnChange = (event) => {
    const selectedColumns = event.target.value;
    // Sort the selected columns to maintain the correct order
    const sortedColumns = selectedColumns.sort((a, b) => a - b);
    setVisibleColumns(sortedColumns);
  };
  useEffect(() => {
    getVendorDropdown();
  }, []);

  return (
    <Box sx={{ m: 4, mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: Colors.charcoalGrey,
            fontFamily: FontFamily.NunitoRegular,
          }}
        >
          Export Vendor Funds Apply
        </Typography>
      </Box>

      <Box
        component={"form"}
        onSubmit={handleSubmit(applyFilter)}
        sx={{
          m: "20px 0 20px 5px",
          p: "10px 20px",
          bgcolor: Colors.feta,
          border: `1px solid ${Colors.iron}`,
          borderRadius: "9px",
        }}
      >
        <Grid
          container
          spacing={1}
          columns={12}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* <Grid item md={2}>
            <InputField
              size={"small"}
              disabled={true}
              label={"Vendor"}
              placeholder={"Vendor"}
              register={register("vendor")}
            />
          </Grid> */}
          {/* <Grid item md={3}>
            <InputField
              size={"small"}
              label={"Vin"}
              placeholder={"Vin"}
              error={errors?.vin?.message}
              register={register("vin")}
            />
          </Grid> */}
          <Grid item md={3}>
            {isAgent || isPortAgent ? (
              <InputField
                size={"small"}
                label={"Manifest No"}
                placeholder={"Manifest No"}
                register={register("manifest_no")}
              />
            ) : isBroker ? (
              <InputField
                size={"small"}
                label={"CMR No"}
                placeholder={"CMR No"}
                register={register("CMR_no")}
              />
            ) : (
              isRecoveryAgent && (
                <InputField
                  size={"small"}
                  label={"VIN"}
                  placeholder={"VIN"}
                  register={register("vin")}
                />
              )
            )}
          </Grid>

          <Grid item xs={12} md={2}>
            <Box
              sx={{
                mt: "11px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <PrimaryButton title={"Search"} type={"submit"} />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ my: 4, p: 2, bgcolor: Colors.whiteSmoke }}>
        <Grid container spacing={2} columns={12}>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                // justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: FontFamily.NunitoRegular, width: "30%" }}
              >
                Vendor Type
              </Typography>
              <Box
                sx={{
                  textAlign: "center",
                  p: 1,
                  width: "70%",
                  bgcolor: Colors.flashWhite,
                  border: "1px solid #B2B5BA",
                  borderRadius: "4px",
                }}
              >
                <Typography variant="body2" sx={{ color: Colors.smokeyGrey }}>
                  {state?.vendor?.customerProfile?.type
                    ? state?.vendor?.customerProfile?.type === "Recovery Agent"
                      ? "Recovery & Shifting Agent"
                      : state?.vendor?.customerProfile?.type
                    : "-" || "-"}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                // justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: FontFamily.NunitoRegular, width: "30%" }}
              >
                Vendor Name
              </Typography>
              <Box
                sx={{
                  textAlign: "center",
                  p: 1,
                  width: "70%",
                  bgcolor: Colors.flashWhite,
                  border: "1px solid #B2B5BA",
                  borderRadius: "4px",
                }}
              >
                <Typography variant="body2" sx={{ color: Colors.smokeyGrey }}>
                  {state?.vendor?.name}
                </Typography>
              </Box>
            </Box>
          </Grid>{" "}
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                // justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: FontFamily.NunitoRegular, width: "30%" }}
              >
                VFA No
              </Typography>
              <Box
                sx={{
                  textAlign: "center",
                  p: 1,
                  width: "70%",
                  bgcolor: Colors.flashWhite,
                  border: "1px solid #B2B5BA",
                  borderRadius: "4px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ cursor: "pointer", color: Colors.smokeyGrey }}
                >
                  VUAF-{state?.id}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                // justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: FontFamily.NunitoRegular, width: "30%" }}
              >
                TT.Apply Date
              </Typography>
              <Box
                sx={{
                  textAlign: "center",
                  p: 1,
                  width: "70%",
                  bgcolor: Colors.flashWhite,
                  border: "1px solid #B2B5BA",
                  borderRadius: "4px",
                }}
              >
                <Typography variant="body2" sx={{ color: Colors.smokeyGrey }}>
                  {moment(state?.created_at).format("YYYY-MM-DD")}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                // justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: FontFamily.NunitoRegular, width: "30%" }}
              >
                Applied Amount$
              </Typography>
              <Box
                sx={{
                  textAlign: "center",
                  p: 1,
                  width: "70%",
                  bgcolor: Colors.flashWhite,
                  border: "1px solid #B2B5BA",
                  borderRadius: "4px",
                }}
              >
                <Typography variant="body2" sx={{ color: Colors.smokeyGrey }}>
                  {CommaSeparator(totalApplied?.toFixed(2))}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                // justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: FontFamily.NunitoRegular, width: "30%" }}
              >
                External Ref No
              </Typography>
              <Box
                sx={{
                  textAlign: "center",
                  p: 1,
                  width: "70%",
                  bgcolor: Colors.flashWhite,
                  border: "1px solid #B2B5BA",
                  borderRadius: "4px",
                }}
              >
                <Typography variant="body2" sx={{ color: Colors.smokeyGrey }}>
                  {state?.external_no ? state?.external_no : "-"}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                // justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: FontFamily.NunitoRegular, width: "30%" }}
              >
                TT Amount$
              </Typography>
              <Box
                sx={{
                  textAlign: "center",
                  p: 1,
                  width: "70%",
                  bgcolor: Colors.flashWhite,
                  border: "1px solid #B2B5BA",
                  borderRadius: "4px",
                }}
              >
                <Typography variant="body2" sx={{ color: Colors.smokeyGrey }}>
                  {state?.fcy_amount
                    ? `${CommaSeparator(
                        parseFloat(
                          parseFloat(state?.fcy_amount) -
                            parseFloat(state?.paid_amount)
                        ).toFixed(2)
                      )}`
                    : "-"}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                // justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: FontFamily.NunitoRegular, width: "30%" }}
              >
                Balance$
              </Typography>
              <Box
                sx={{
                  textAlign: "center",
                  p: 1,
                  width: "70%",
                  bgcolor: Colors.flashWhite,
                  border: "1px solid #B2B5BA",
                  borderRadius: "4px",
                }}
              >
                <Typography variant="body2" sx={{ color: Colors.smokeyGrey }}>
                  {CommaSeparator(totalBalance?.toFixed(2))}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Grid item md={11}>
        {vendorCosting?.length > 0 && (
          <Box>
            <Grid container mb={2}>
              <Grid item xs={5}>
                <FormControl>
                  <InputLabel>Columns</InputLabel>
                  <Select
                    size={"small"}
                    multiple
                    value={visibleColumns}
                    label={"Columns"}
                    onChange={handleColumnChange}
                    renderValue={() => "Show/Hide"}
                  >
                    {tableHead.map((column, index) => {
                      if (column !== "Applied Amount") {
                        return (
                          <MenuItem key={index} value={index}>
                            <Checkbox
                              checked={visibleColumns.includes(index)}
                            />
                            <ListItemText primary={column} />
                          </MenuItem>
                        );
                      } else {
                        return null;
                      }
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {loader ? (
              <CircularProgress />
            ) : (
              vendorCosting?.length > 0 && (
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: "0px 8px 18px 0px #9B9B9B1A",
                    borderRadius: 2,
                    maxHeight: "calc(100vh - 330px)",
                  }}
                >
                  <Table stickyHeader sx={{ minWidth: 500 }}>
                    {/* Table Header */}
                    <TableHead>
                      <TableRow>
                        {visibleColumns.map((index) => (
                          <Cell key={index}>{tableHead[index]}</Cell>
                        ))}
                      </TableRow>
                    </TableHead>

                    {/* Table Body */}
                    <TableBody>
                      {!loader ? (
                        vendorCosting?.length > 0 ? (
                          <Fragment>
                            {vendorCosting.map((item, rowIndex) => {
                              const total = isAgent
                                ? item?.payment?.agent_charges +
                                  item?.payment?.border_charges
                                : isBroker
                                ? item?.payment?.broker_charges
                                : isRecoveryAgent
                                ? item?.payment?.recovery_charges +
                                  item?.payment?.shifting_charges
                                : parseFloat(
                                    item?.payment?.country_charges ?? 0
                                  );
                              const paidAmount = isAgent
                                ? item?.payment?.agent_charged_paid +
                                  item?.payment?.border_charged_paid
                                : isBroker
                                ? item?.payment?.broker_charged_paid
                                : isRecoveryAgent
                                ? item?.payment?.recovery_charged_paid +
                                  item?.payment?.shifting_charged_paid
                                : parseFloat(
                                    item?.payment?.country_charged_paid ?? 0
                                  );

                              const balance =
                                parseFloat(total) - parseFloat(paidAmount);

                              const status =
                                balance === 0
                                  ? "Paid"
                                  : parseFloat(balance) === parseFloat(total)
                                  ? "UnPaid"
                                  : "Partial Paid";

                              const calcAmountRow = calcAmount.find(
                                (e) => e.id === item.id
                              );
                              // const rowBalance = calcAmountRow?.balance;
                              const rowBalance = rowBalanced[item?.id] ?? 0;

                              // Check if the row is active (checked)
                              const isActive =
                                selectedClientBooking.indexOf(item?.id) !== -1;

                              // Set fcyammount to 0 if the row is not active
                              let fcyammount;
                              if (state?.fcy_amount) {
                                fcyammount = !isActive
                                  ? Math.min(
                                      parseFloat(rowBalance),
                                      parseFloat(state?.fcy_amount) -
                                        parseFloat(state?.paid_amount)
                                    )
                                  : 0;
                              } else {
                                fcyammount = !isActive
                                  ? Math.min(
                                      parseFloat(rowBalance),
                                      parseFloat(state?.client_amount)
                                    )
                                  : 0;
                              }

                              return (
                                <Row
                                  key={rowIndex}
                                  sx={{
                                    bgcolor: rowIndex % 2 !== 0 && "#EFF8E7",
                                  }}
                                >
                                  {visibleColumns.map((colIndex) => (
                                    <Cell key={colIndex}>
                                      {renderCellContent(
                                        colIndex,
                                        item,
                                        isActive,
                                        total,
                                        status,
                                        rowBalance,
                                        fcyammount
                                      )}
                                    </Cell>
                                  ))}
                                </Row>
                              );
                            })}
                          </Fragment>
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={visibleColumns?.length + 1}
                              align="center"
                              sx={{ fontWeight: 600 }}
                            >
                              No Data Found
                            </TableCell>
                          </TableRow>
                        )
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={visibleColumns?.length + 2}
                            align="center"
                            sx={{ fontWeight: 600 }}
                          >
                            <Box className={classes.loaderWrap}>
                              <CircularProgress />
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            )}

            {/* Pagination */}
            {vendorCosting?.length > 0 && (
              <Pagination
                currentPage={currentPage}
                pageSize={pageLimit}
                onPageSizeChange={(size) =>
                  getVendorCosting(1, size.target.value)
                }
                tableCount={vendorCosting?.length}
                totalCount={totalCount}
                onPageChange={(page) => getVendorCosting(page, "")}
              />
            )}
          </Box>
        )}
      </Grid>
      <Grid
        container
        spacing={2}
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Grid item xs={12} md={3} sm={3}>
          <Controller
            name="notes"
            control={control2}
            defaultValue={state?.notes || ""}
            render={({ field, fieldState }) => (
              <InputField
                size="small"
                label="Notes"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={fieldState?.error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <PrimaryButton
              title="Apply"
              onClick={applyFund}
              disabled={loading}
            />
          </Box>
        </Grid>
      </Grid>
      <Drawer anchor="right" open={loading} onClose={() => setLoading(false)}>
        <Box sx={{ width: 300, p: 3, textAlign: "center" }}>
          <Typography variant="h6">Applying Funds</Typography>
          <Box sx={{ mt: 3 }}>
            <CircleLoading />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

export default ExportVendorFundsApply;
